<template>
  <b-card
    header-tag="header"
    footer-tag="footer"
    header-class="v-card-header"
    footer-class="v-card-footer"
    no-body
  >
    <template v-slot:header>
      <h2 class="v-card-header__title">
        <div class="slds-icon_container slds-icon-standard-template">
          <svg class="type-icon slds-icon">
            <use
              xlink:href="@/assets/icons/standard-sprite/svg/symbols.svg#template"
            ></use>
          </svg>
        </div>

        <b-link
          :to="{
            name: 'DataPalette',
            params: {
              appId: app.uuid,
              appName: app.name,
              appTestRecord: app.testRecord
            }
          }"
        >
          <span>{{ app.name }}</span>
        </b-link>
      </h2>
      <card-action-menu :app="app"></card-action-menu>
      <archive-confirm :appId="app.uuid"></archive-confirm>
    </template>

    <div :class="descClassName" class="slds-truncate" :title="app.description">
      {{ app.description }}
    </div>

    <b-card-body v-if="isShowingBody">
      <div class="hd-row">{{ nameText }}</div>
      <b-list-group flush :class="'l-group'">
        <b-list-group-item
          class="linked-content"
          v-for="i in linkedItems"
          :key="i.uuid"
        >
          <div class="sub-card-hd">
            <div
              class="slds-icon_container slds-icon-standard-template"
              v-if="i.isTemplate"
            >
              <svg class="type-icon mini slds-icon">
                <use
                  xlink:href="@/assets/icons/standard-sprite/svg/symbols.svg#template"
                ></use>
              </svg>
            </div>
            <div
              class="slds-icon_container slds-icon-standard-dashboard-ea"
              v-else
            >
              <svg class="type-icon mini slds-icon">
                <use
                  xlink:href="@/assets/icons/standard-sprite/svg/symbols.svg#dashboard_ea"
                ></use>
              </svg>
            </div>
            <b-link
              :to="{
                name: 'DataPalette',
                params: {
                  appId: i.uuid,
                  appName: i.name,
                  appTestRecord: i.testRecord
                }
              }"
            >
              <span class="linked-content-name">{{ i.name }}</span>
            </b-link>
            <card-action-menu :app="i" :is-mini-mode="true"></card-action-menu>
            <archive-confirm :appId="i.uuid"></archive-confirm>
          </div>
          <div
            :class="descClassName"
            class="slds-truncate card-table-desc"
            :title="i.description"
          >
            {{ i.description }}
          </div>
        </b-list-group-item>
      </b-list-group>
      <div class="deployment-table-container">
        <div class="spinner-container" v-if="isLoadingTable">
          <div class="loader"></div>
        </div>
        <b-table
          striped
          hover
          :fields="fields"
          :items="app.deployments"
          :empty-text="failedText"
          show-empty
          v-if="!isLoadingTable && dpFetchingFailed"
        >
          <template #empty="scope">
            <div>{{ scope.emptyText }}</div>
          </template>
        </b-table>

        <b-table
          striped
          hover
          :fields="fields"
          :items="app.deployments"
          v-if="shouldShowDeploymentTable"
        >
          <template v-slot:cell(Phone_vod__c)="row">
            <b-form-checkbox
              v-model="row.item.Phone_vod__c"
              disabled
            ></b-form-checkbox>
          </template>
        </b-table>
      </div>
    </b-card-body>

    <template v-slot:footer v-if="shouldShowBtn">
      <div class="footer-container">
        <a>
          <span
            class="expand-label"
            @click="hideBody(app.uuid)"
            v-if="isShowingBody"
          >
            {{ $t("hideDetails") }}
          </span>
          <span
            class="expand-label"
            @click="showBody(app.uuid)"
            v-if="!isShowingBody"
          >
            {{ $t("showDetails") }}
          </span>
        </a>
      </div>
    </template>
  </b-card>
</template>

<script>
  import { mapState } from "vuex";
  import store from "@/store";
  import services from "@/services";
  import CardActionMenu from "./CardActionMenu";
  import ArchiveConfirm from "./ArchiveConfirm";
  import { MUTATION_NAME } from "@/static/global-variables";
  import utils from "@/utils";

  export default {
    name: "TemplateCard",
    props: {
      app: Object,
      linkedItems: Array
    },
    components: {
      CardActionMenu,
      ArchiveConfirm
    },
    data() {
      return {
        isShowingBody: false,
        isLoadingTable: false,
        dpFetchingFailed: false,
        contentClone: null
      };
    },
    computed: {
      ...mapState({
        apps: "apps",
        messages: "messages",
        htmlReportFieldLabels: "htmlReportFieldLabels",
        objectDescribe: "objectDescribe"
      }),
      descClassName() {
        return this.isShowingBody ? "detail-card" : "overview-card";
      },
      nameText() {
        return this.getMessageText(this.messages.NAME);
      },
      failedText() {
        return this.getMessageText(this.messages.FAILED_TO_LOAD_DEPLOYMENTS);
      },
      fieldFLS() {
        return utils.getDeploymentTableFLS(this.objectDescribe);
      },
      fields() {
        return utils.getDeployTableFieldList(
          this.fieldFLS,
          this.htmlReportFieldLabels
        );
      },
      shouldShowDeploymentTable() {
        return (
          !this.isLoadingTable &&
          !this.dpFetchingFailed &&
          this.app.deployments.length > 0
        );
      },
      shouldShowBtn() {
        return this.fieldFLS.studioIdFLS.hasRead;
      }
    },
    methods: {
      hideBody() {
        this.isShowingBody = false;
      },
      showBody() {
        let component = this;
        let currentApps = this.apps;
        const fls = component.fieldFLS;

        component.isShowingBody = true;
        component.isLoadingTable = true;

        const fieldFLS = {
          includeProfileName: fls.reportProfileNameFLS.hasRead,
          includeORTName: fls.reportORTNameFLS.hasRead,
          includePlatform: fls.platformFLS.hasRead,
          includeStudioVersion: fls.studioVersionFLS.hasRead,
          includeName: fls.nameFLS.hasRead,
          includeRecordType: fls.recordTypeFLS.hasRead,
          includePhone: fls.phoneFLS.hasRead,
          includeStatus: fls.statusFLS.hasRead,
          includeStatusDetail: fls.statusDetailsFLS.hasRead,
          includeStudioId: fls.studioIdFLS.hasRead,
          includeExternalId: fls.externalIdFLS.hasRead,
          includeVisibilityCriteria: fls.visibilityCriteriaFLS.hasRead,
          includeSharingGroup: fls.sharingGroupFLS.hasRead
        };

        services.fetchDeploymentsForApp(component.app.uuid, fieldFLS).then(
          resp => {
            if (!Array.isArray(resp.records)) {
              component.dpFetchingFailed = true;
            } else {
              let updatedApps = utils.addDeploymentsDataToApp(
                currentApps,
                component.app.uuid,
                utils.pruneSFRecords(resp.records)
              );

              store.commit(MUTATION_NAME.SET_APPS, updatedApps);
            }
            component.isLoadingTable = false;
          },
          () => {
            component.dpFetchingFailed = true;
            component.isLoadingTable = false;
          }
        );
      }
    }
  };
</script>

<style scoped>
  .v-card {
    padding: 0;
    background: #fff;
    border: 1px solid #dddbda;
    border-radius: 0.25rem;
    background-clip: padding-box;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.1);
  }

  .v-card-header {
    display: flex;
    padding: 0.75rem 1rem 0 0.75rem;
    background: #fff;
    border: none;
    min-width: 500px;
  }

  .v-card-header__title {
    display: flex;
    font-size: 1rem;
    line-height: 1.25;
    font-weight: inherit;
    margin: 0;
    padding: 0;
    align-items: center;
  }

  .v-card-header__title a {
    color: inherit;
    font-weight: 700;
    text-decoration: none;
    transition: color 0.1s linear;
    cursor: pointer;
    background-color: transparent;
    font-size: 1rem;
    line-height: 1.25;
  }

  .v-card-header__title a:hover {
    text-decoration: underline;
    color: #005fb2;
  }

  .v-card-header__title a:focus {
    text-decoration: underline;
    color: #005fb2;
  }

  .v-card-header__title .slds-icon_container {
    margin-right: 1rem;
  }

  .v-card-footer {
    padding: 0.5rem 1rem;
    text-align: center;
    font-size: 0.8125rem;
    border-top: 1px solid #dddbda;
    background: #fff;
  }

  .v-card-footer a {
    color: #006dcc;
    text-decoration: none;
    transition: color 0.1s linear;
    cursor: pointer;
  }

  .v-card-footer a:focus,
  .v-card-footer a:hover {
    text-decoration: underline;
    color: #005fb2;
    outline: 0;
  }

  .v-card-header .dropdown {
    margin-left: auto;
  }

  .type-icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .hd-row {
    padding-left: 1.25rem;
    height: 45px;
    border-top: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    display: flex;
    align-items: center;
  }

  .sub-card-hd {
    display: flex;
    align-items: center;
  }

  .mini {
    width: 1.2rem;
    height: 1.2rem;
  }

  .linked-content-name {
    padding: 0 0.25rem;
  }

  .deployment-table-container {
    margin-top: 1rem;
  }

  .l-group {
    border-bottom: 1px solid var(--NAV_TABS_BORDER_GREY);
  }
</style>
